"use client";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { RrefreshToken } from "../actions/auth/refresh-token";

export default function NotFound() {
  const handleRefreshToken = async () => {
    const newToken = await RrefreshToken();
    console.log(newToken, "newToken");
  };
  
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-linear-to-br from-background via-background to-muted/10 p-4">
      <div className="max-w-lg w-full mx-auto text-center space-y-8 animate-fade-in-up">
        {/* Animated 404 with floating elements */}
        <div className="relative flex justify-center mb-8">
          <div className="relative group">
            {/* Background glow effect */}
            <div className="absolute inset-0 bg-primary/5 rounded-full blur-3xl scale-150 group-hover:bg-primary/10 transition-all duration-500"></div>

            {/* Main 404 text */}
            <div className="relative text-8xl font-bold bg-linear-to-br from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent select-none">
              404
            </div>

            {/* Floating decorative elements */}
            <div className="absolute -top-4 -right-4 w-3 h-3 bg-primary/40 rounded-full animate-bounce delay-100"></div>
            <div className="absolute -bottom-2 -left-6 w-2 h-2 bg-primary/60 rounded-full animate-bounce delay-300"></div>
            <div className="absolute top-1/2 -right-8 w-1.5 h-1.5 bg-primary/50 rounded-full animate-bounce delay-500"></div>
          </div>
        </div>

        {/* Enhanced error message with icon */}
        <div className="space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <div className="p-3 bg-muted/50 rounded-full">
              <svg
                className="h-6 w-6 text-muted-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-foreground">
            Oops! Page Not Found
          </h1>

          <p className="text-muted-foreground text-lg leading-relaxed max-w-md mx-auto">
            The page you&apos;re looking for seems to have wandered off.
            Don&apos;t worry, let&apos;s get you back on track!
          </p>
        </div>

        {/* Enhanced action buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
          <Button
            asChild
            size="lg"
            className="group relative overflow-hidden bg-primary hover:bg-primary/90 transition-all duration-300 transform hover:scale-105 hover:shadow-lg">
            <Link href="/dashboard" className="flex items-center space-x-2">
              <svg
                className="w-4 h-4 transition-transform group-hover:translate-x-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z"
                />
              </svg>
              <span>Go to Dashboard</span>
            </Link>
          </Button>

          <Button
            variant="outline"
            asChild
            size="lg"
            className="group border-2 hover:border-primary/50 transition-all duration-300 transform hover:scale-105">
            <Link href="/" className="flex items-center space-x-2">
              <svg
                className="w-4 h-4 transition-transform group-hover:-translate-x-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span>Back to Home</span>
            </Link>
          </Button>
        </div>

        {/* Enhanced help section */}
        <div className="pt-8 space-y-4">
          <div className="h-px bg-linear-to-br from-transparent via-border to-transparent"></div>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-6 text-sm">
            <Link
              href="/support"
              className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors duration-200 group">
              <svg
                className="w-4 h-4 group-hover:scale-110 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Contact Support</span>
            </Link>

            <div className="hidden sm:block w-px h-4 bg-border"></div>

            <Link
              href="/help"
              className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors duration-200 group">
              <svg
                className="w-4 h-4 group-hover:scale-110 transition-transform"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              <span>Help Center</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out;
        }
      `}</style>
    </div>
  );
}
